import type { ShopPriceVariant, ShopProduct } from "./types";
import type { CartItem } from "./cartStore";

/** The variant shown/added by default when the enduser hasn't made an explicit choice yet — the cheapest. */
export const getDefaultVariant = (product: ShopProduct): ShopPriceVariant =>
  product.variants.reduce((cheapest, variant) => (variant.amount < cheapest.amount ? variant : cheapest));

export const findVariant = (product: ShopProduct, stripePriceId: string): ShopPriceVariant | undefined =>
  product.variants.find((variant) => variant.stripePriceId === stripePriceId);

/**
 * A cart item whose product/price variant still exist in the given products list — i.e. the admin
 * hasn't since removed it from the Shop block's configuration. Cart items are keyed only by Stripe
 * product/price ID, with nothing tying them to a specific block config, so every consumer that reads
 * cart items for display or checkout needs to filter through this before trusting an item is still
 * purchasable.
 */
export const isValidCartItem = (item: CartItem, products: ShopProduct[]): boolean => {
  const product = products.find((p) => p.stripeProductId === item.stripeProductId);
  return product !== undefined && findVariant(product, item.stripePriceId) !== undefined;
};

/** Cart items still valid against the given products list, in cart order. */
export const filterValidCartItems = (items: CartItem[], products: ShopProduct[]): CartItem[] =>
  items.filter((item) => isValidCartItem(item, products));
